/** Minimal shape for list items and full `Goal` context objects. */
export type GoalLike = {
  id?: number;
  title?: string;
  title_render?: string;
  featured_image_url?: string;
  icon?: string;
  acf?: { index?: string; name?: string; goal_index?: string; [key: string]: unknown };
};

/** True when URL is a bundled local SDG icon (not a WP media URL). */
function isLocalSdgIconUrl(url: string): boolean {
  return /\/sdg-icons\/sdg-\d+\.(svg|png)(\?|$)/i.test(url);
}

/**
 * Local fallback icon path by SDG goal number (1–17), not WP post id.
 */
export function getLocalGoalIconUrl(goal: GoalLike): string {
  const n = parseGoalNumber(goal);
  return n ? `/sdg-icons/sdg-${n}.svg` : '';
}

/**
 * Prefer WordPress goal featured image; fall back to bundled `/sdg-icons/sdg-N.svg`.
 */
export function getGoalIconUrl(goal: GoalLike | null | undefined): string {
  if (!goal) return '';
  const fromWp = (goal.featured_image_url || '').trim();
  if (fromWp && !isLocalSdgIconUrl(fromWp)) return fromWp;
  return getLocalGoalIconUrl(goal) || fromWp;
}

/** Swap a broken WP image to the local SDG icon (safe for img onError). */
export function onGoalIconError(
  event: { currentTarget: HTMLImageElement },
  goal: GoalLike | null | undefined
): void {
  const local = getLocalGoalIconUrl(goal || {});
  const img = event.currentTarget;
  if (!local || img.dataset.localFallback === '1') return;
  img.dataset.localFallback = '1';
  img.src = local;
}

/**
 * SDG goal number (1–17) from ACF/meta or parsed from title fields.
 * Uses a boundary so index "1" does not match the leading "1" in "15 …".
 */
export function parseGoalNumber(goal: GoalLike): string {
  const rawIdx = goal.acf?.index ?? (goal.acf as { goal_index?: string } | undefined)?.goal_index;
  if (rawIdx != null && String(rawIdx).trim() !== '') {
    const digits = String(rawIdx).replace(/\D/g, '');
    if (digits) return digits.replace(/^0+(?=\d)/, '') || digits;
  }
  const sources = [goal.title_render, goal.title, goal.acf?.name];
  for (const t of sources) {
    if (!t) continue;
    const s = String(t).trim();
    const verbal = s.match(/^\s*Goal\s+(\d{1,2})(?!\d)/i);
    if (verbal) return verbal[1];
    const lead = s.match(/^\s*(\d{1,2})(?!\d)/);
    if (lead) return lead[1];
  }
  return '';
}

/** Remove stray index / colon prefixes so we can prepend `Goal N: ` once. */
export function stripBrokenGoalHeadingPrefix(s: string): string {
  let t = s.trim();
  if (/^\s*Goal\s+\d{1,2}\s*:/i.test(t)) {
    return t.replace(/^\s*Goal\s+\d{1,2}\s*:\s*/i, '').trim();
  }
  t = t.replace(/^\s*Goal\s+\d{1,2}\s*[·–-]\s*/i, '').trim();
  t = t.replace(/^\s*:\s*/, '').trim();
  t = t.replace(/^\s*\d{1,2}(?!\d)\s*[:·–-]?\s*/, '').trim();
  return t;
}

/**
 * Canonical heading for SDG goal cards: "Goal 1: …".
 * Defensive against API/meta that only expose a description or a broken ": …" fragment.
 */
export function getGoalDisplayTitle(goal: GoalLike): string {
  const raw = (goal.acf?.name || goal.title_render || goal.title || '').trim();
  if (!raw) {
    const n = parseGoalNumber(goal);
    return n ? `Goal ${n}` : '';
  }
  if (/^\s*Goal\s+\d{1,2}\s*:/i.test(raw)) {
    return raw;
  }
  const n = parseGoalNumber(goal);
  const desc = stripBrokenGoalHeadingPrefix(raw);
  if (n && desc) return `Goal ${n}: ${desc}`;
  if (n && !desc) return `Goal ${n}`;
  return desc || raw;
}
